import { useApiMutation, useApiPolling } from "@/api";
import {
    createCreditBoxFromSfdc,
    updateOpportunitiesFromSfdc,
    getTaskStatus,
    runCreditBoxes,
    getCreditBoxesResultsTask,
} from ".";
import {
    CreditBoxesResultsPayload,
    CreditBoxRunPayload,
    CreditBoxStatus,
} from "./CreditBox.types";
import { useCallback, useState } from "react";
import { downloadFile } from "@/utils/misc/downloadFile";

export const TASK_ID_KEY = "creditbox_task_id";
const saveTaskId = (taskId: string) => {
    try {
        localStorage.setItem(TASK_ID_KEY, taskId);
    } catch (error) {
        console.warn("Failed to save taskId to localStorage:", error);
    }
};

const getStoredTaskId = (): string | null => {
    try {
        return localStorage.getItem(TASK_ID_KEY);
    } catch (error) {
        console.warn("Failed to read taskId from localStorage:", error);
        return null;
    }
};

const clearStoredTaskId = () => {
    try {
        localStorage.removeItem(TASK_ID_KEY);
    } catch (error) {
        console.warn("Failed to clear taskId from localStorage:", error);
    }
};

export const useCreditboxes = () => {
    const [displayMessage, setDisplayMessage] = useState<string>("");
    const [isResumedTask, setIsResumedTask] = useState(false);
    const {
        data: task,
        isPolling,
        startPolling,
        stopPolling,
        isError: isTaskError,
    } = useApiPolling(getTaskStatus, {
        onPollSuccess(data) {
            if (data?.meta?.displayMsg) {
                setDisplayMessage(data.meta.displayMsg);
            }
            if (data?.meta?.presignedS3Url) {
                downloadFile(data.meta.presignedS3Url);
            }
            if (data?.state === CreditBoxStatus.Failure) {
                stopPolling();
                clearStoredTaskId();
                setIsResumedTask(false);
            }
            if (data?.state === CreditBoxStatus.Success) {
                stopPolling();
                clearStoredTaskId();
                setIsResumedTask(false);
            }
        },
    });

    const resumeStoredTask = useCallback(() => {
        const storedTaskId = getStoredTaskId();
        if (storedTaskId && !isPolling) {
            startPolling({ taskId: storedTaskId });
            setIsResumedTask(true);
            return true;
        }
        return false;
    }, [startPolling, isPolling]);

    const { mutate: generateCreditBoxes, isError: isGenerateCreditBoxesError } =
        useApiMutation(createCreditBoxFromSfdc, {
            onSuccess(data) {
                if (data.taskId) {
                    startPolling(data);
                    saveTaskId(data.taskId);
                }
            },
        });
    const { mutate: updateOpportunities, isError: isUpdateOpportunitiesError } =
        useApiMutation(updateOpportunitiesFromSfdc, {
            onSuccess(data) {
                if (data.taskId) {
                    startPolling(data);
                    saveTaskId(data.taskId);
                }
            },
        });

    const { mutate: runCreditBox, isError: isRunCreditBoxError } =
        useApiMutation(runCreditBoxes, {
            onSuccess(data) {
                if (data.taskId) {
                    startPolling(data);
                    saveTaskId(data.taskId);
                }
            },
        });
    const {
        mutate: getCreditBoxesResults,
        isError: isGetCreditBoxesResultsSpreadsheetError,
    } = useApiMutation(getCreditBoxesResultsTask, {
        onSuccess(data) {
            if (data.taskId) {
                startPolling(data);
                saveTaskId(data.taskId);
            }
        },
    });

    const isError =
        isTaskError ||
        isGenerateCreditBoxesError ||
        isUpdateOpportunitiesError ||
        isRunCreditBoxError ||
        isGetCreditBoxesResultsSpreadsheetError ||
        task?.state === CreditBoxStatus.Failure;

    const isSuccess = task?.state === CreditBoxStatus.Success;

    const stopTask = useCallback(() => {
        stopPolling();
        clearStoredTaskId();
        setIsResumedTask(false);
    }, [stopPolling]);

    return {
        task,
        isPolling,
        startGenerateCreditBoxes: () => generateCreditBoxes(),
        startUpdateOpportunities: () => updateOpportunities(),
        startRunCreditBox: (payload: CreditBoxRunPayload) =>
            runCreditBox(payload),
        startGetCreditBoxesResultsTask: (payload: CreditBoxesResultsPayload) =>
            getCreditBoxesResults(payload),
        stopPolling: stopTask,
        resumeStoredTask,
        isError,
        isSuccess,
        displayMessage,
        isResumedTask,
    };
};
